import type { htmlSafe } from '@ember/template';
type SafeString = ReturnType<typeof htmlSafe>;

function getLongestWordSplit(text: string, count: number) {
  if (count === 1) {
    return text;
  }
  const results = [];
  const delimiterList = [' ', '・'];

  const nominalWidth = text.length / count;
  for (let piece = 0; piece < count; piece++) {
    let left = piece * nominalWidth;
    let right = (piece + 1) * nominalWidth;
    let leftIndex = -1;
    for (const delimeter of delimiterList) {
      const currLeftIndex = text.substring(0, left).lastIndexOf(delimeter);
      if (leftIndex === -1) {
        if (currLeftIndex !== -1) leftIndex = currLeftIndex;
      } else {
        if (currLeftIndex !== -1) leftIndex = leftIndex < currLeftIndex ? leftIndex : currLeftIndex;
      }
    }
    if (leftIndex === -1) {
      left = 0;
    } else {
      left = leftIndex + 1;
    }
    let rightIndex = -1;
    let isDelimiterSpace = true;
    for (const delimeter of delimiterList) {
      const currRightIndex = text.indexOf(delimeter, right - 1);
      if (rightIndex === -1) {
        if (currRightIndex !== -1) {
          rightIndex = currRightIndex;
          isDelimiterSpace = delimeter === ' ';
        }
      } else {
        if (currRightIndex !== -1) {
          if (currRightIndex < rightIndex) {
            rightIndex = currRightIndex;
            isDelimiterSpace = delimeter === ' ';
          }
        }
      }
    }
    if (rightIndex === -1) {
      right = text.length;
    } else {
      right = rightIndex < text.length && !isDelimiterSpace ? rightIndex + 1 : rightIndex;
    }
    results.push(text.substring(left, right));
  }
  let item = '';
  results.forEach((value) => {
    if (value.length > item.length) {
      item = value;
    }
  });
  return item;
}

function getMeasuredTextWidth(canvas: HTMLCanvasElement, font: string, weight: string, size: number, text: string) {
  const context = canvas.getContext('2d');
  if (context) {
    context.font = `${weight} ${size}px ${font}`;
    return context.measureText(text).width;
  } else {
    return 0;
  }
}

function fitText(
  font: string,
  weight: string,
  text: string | SafeString,
  height: number,
  width: number,
  maxSize: number,
  minSize: number
): number {
  const canvas = document.createElement('canvas');
  const sizes = [];

  let actualText = '';
  if (typeof text === 'string') {
    actualText = text;
  } else {
    const pElem = document.createElement('p');
    pElem.innerHTML = (text as SafeString).toHTML();
    actualText = pElem.innerText;
  }

  for (let count = 0; count < Math.floor(height / (minSize + 4)) + 1; count++) {
    // max row count limited by font height of minimum font size
    const trialText = getLongestWordSplit(actualText, count + 1);
    let size = Math.round(Math.min(maxSize, height / (count + 1) - 8)); // max font size limited fitting the current number of proposed rows
    let testWidth = getMeasuredTextWidth(canvas, font, weight, size, trialText);
    while (testWidth > width) {
      const oldSize = size;
      size = Math.floor((size * width) / testWidth);
      if (size >= oldSize) {
        // it is remotely possible that, due to round-off errors, we might get a size freeze or oscillation - disrupt it.
        size = oldSize;
        break;
      }
      testWidth = getMeasuredTextWidth(canvas, font, weight, size, trialText); // big number
    }
    sizes.push(size);
  }
  return Math.max(...sizes);
}

export { fitText };
